import PropTypes from 'prop-types';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Typography from '@mui/material/Typography';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import QAmountField from 'components/QAmountField';
import QSwitch from 'components/QSwitch';

import { dateSelectors } from 'app-components/flux/date';

import Divider from '@mui/material/Divider';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';

const useStyles = makeStyles()((theme) => ({
  rolloversRoot: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(3),
  },
  rollRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  rolloverDivider: {
    marginTop: 12,
    marginBottom: 12,
  },
  hiddenRow: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
  },
  textSecondaryColor: {
    color: theme.palette.text.secondary,
  },
  helperText: {
    maxWidth: '288px',
    width: '288px',
    display: 'flex',
  },
  amountField: {
    width: 200,
  },
  monthDesc: {
    marginLeft: 30,
    alignSelf: 'center',
  },
  infoBackground: {
    backgroundColor: theme.palette.info.light,
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'row',
    width: 310,
    padding: '8px 16px 8px 16px',
  },
  icon: {
    width: 20,
    height: 20,
    marginRight: 5,
    color: theme.palette.icon.light,
  },
}));

function RolloverToggle(props) {
  const { setIsRolledOver, isRolledOver, startDate, resetRolloverAmount, setResetRolloverAmount, initRolloverSetting } =
    props;
  const { classes, cx } = useStyles();

  const currentDate = useSelector(dateSelectors.getCurrentDate);
  const currentMonth = currentDate.startOf('month').toFormat('MMMM');
  const startDateFormatted = startDate ? DateTime.fromISO(startDate).startOf('month').toFormat('MMMM yyyy') : null;
  const displayMonth = startDateFormatted || currentMonth;
  const displayLabel = initRolloverSetting ? 'Current rollover' : 'Opening rollover';

  return (
    <div className={classes.rolloversRoot}>
      <Divider className={classes.rolloverDivider} />
      <div className={classes.rollRow}>
        <Typography variant='paragraphRegular'>Roll remaining balance over each month.</Typography>
        <InfoTooltipIcon
          size='medium'
          tooltipPlacement='top'
          tooltipText='Begin each month with the previous month’s balance. If you plan $100 this month but only spend $80, you’ll begin next month with +$20. If you plan $100 but spend $120, you’ll start next month with -$20.'
        />
        <QSwitch checked={isRolledOver} onChange={() => setIsRolledOver(!isRolledOver)} />
      </div>
      {isRolledOver && !initRolloverSetting ? (
        <div className={classes.hiddenRow}>
          <QAmountField
            editable
            id='reset-amount'
            variant={'filled'}
            label={displayLabel}
            value={resetRolloverAmount}
            onChange={(e) => setResetRolloverAmount(Number(e.target.value))}
            textFieldVariant={'filled'}
            className={classes.amountField}
            margin='normal'
            expectedSign='positive'
          />
          <div className={classes.monthDesc}>
            <Typography variant='paragraphRegular' className={cx(classes.helperText, classes.textSecondaryColor)}>
              {'Rollover start date'}
            </Typography>
            <Typography variant='body' className={cx(classes.helperText)}>
              {displayMonth}
            </Typography>
          </div>
        </div>
      ) : null}
      {!isRolledOver && initRolloverSetting ? (
        <div className={classes.infoBackground}>
          <InfoOutlinedIcon className={classes.icon} />
          <Typography variant='paragraphRegular'>{`Rollovers will be turned off starting in ${displayMonth}.`}</Typography>
        </div>
      ) : null}
    </div>
  );
}

RolloverToggle.propTypes = {
  setIsRolledOver: PropTypes.func,
  isRolledOver: PropTypes.bool,
  startDate: PropTypes.any,
  setResetRolloverAmount: PropTypes.func,
  resetRolloverAmount: PropTypes.number,
  initRolloverSetting: PropTypes.bool,
};

export default React.memo(RolloverToggle);
